import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Heart, MagnifyingGlass, Tag, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import { SiteShell, useSite } from "@/components/site/SiteShell";
import { InventoryFilters } from "@/components/site/InventoryFilters";
import { PortalLightbox } from "@/components/PortalLightbox";
import { QuoteDialog } from "@/components/site/QuoteDialog";
import { Pager, ShopToolbar, scrollToTopOf } from "@/components/site/Shop";
import { ArrowLink, SplitText } from "@/components/site/Motion";
import { FAMILIES, familiesOf } from "@/lib/colorFamily";
import { pageCopy } from "@/lib/siteMenu";
import { remnantFit } from "@/lib/remnantFit";
import { clearRecent, getRecent } from "@/lib/recentlyViewed";
import { usePageHead } from "@/lib/siteContent";
import { toggleWishlist, useWishlist } from "@/lib/wishlist";
import { API } from "@/lib/api";
import { useGridCols } from "@/lib/useGridCols";

const EMPTY = { brand: [], color: [], finish: [], material: [], type: [], thickness: [] };
const PAGE = 12;
const SORTS = [
  ["featured", "Default sorting"],
  ["new", "Sort by newest arrivals"],
  ["name", "Sort by name: A to Z"],
  ["sqft", "Sort by size: largest first"],
  ["brand", "Sort by brand"],
];
// masonry rhythm for the grid view
const RATIOS = ["aspect-[4/5]", "aspect-[3/4]", "aspect-[1/1]", "aspect-[4/5]", "aspect-[5/6]", "aspect-[3/4]"];

const photoUrl = (p, w = 900) => `${API}/public/showroom/photo/${p}?w=${w}`;

const tally = (values) => {
  const counts = new Map();
  values.filter(Boolean).forEach((v) => counts.set(v, (counts.get(v) || 0) + 1));
  return [...counts.entries()].sort((a, b) => a[0].localeCompare(b[0]));
};

const dimsOf = (i) => {
  const d = ((i.size || "").match(/[\d.]+/g) || []).map((v) => Number(v).toLocaleString(undefined, { maximumFractionDigits: 1 }));
  const size = d.length >= 2 && i.sqft > 0 ? `${d[0]}" × ${d[1]}"` : "Size on request";
  return [size, i.thickness, i.finish].filter(Boolean).join(" · ");
};

const stockOf = (i) =>
  i.special_order ? "Available to order" : i.kind === "Remnant" ? "Remnant" : i.qty > 1 ? `${i.qty} in stock` : "1 in stock";

/* ── Nordic portfolio-masonry card ── */
const Tile = ({ i, list, saved, onGallery, onOpen, onAsk, onWish, ratio }) => {
  const canHold = !i.special_order && i.qty > 0;
  const ask = canHold ? (
    <button type="button" data-testid={`public-hold-${i.key}`} onClick={() => onAsk(i, true)} className="s-shop-btn !relative !h-11 flex-none px-5">
      <Tag size={14} weight="bold" /> Hold this slab
    </button>
  ) : (
    <button type="button" data-testid={`public-quote-${i.key}`} onClick={() => onAsk(i, false)} className="s-shop-btn !relative !h-11 flex-none px-5">
      Get a quote
    </button>
  );

  if (list) {
    return (
      <article data-testid={`public-tile-${i.key}`} className="s-shop-card group grid grid-cols-[8rem_1fr] gap-5 border-b border-[color:var(--s-line)] pb-8 sm:grid-cols-[15rem_1fr] sm:gap-8">
        <div className="s-fig relative aspect-[399/449] bg-[color:var(--s-line)]">
          {i.photo ? (
            <button type="button" data-testid={`public-photo-${i.key}`} onClick={() => onGallery(i)} className="block h-full w-full cursor-zoom-in">
              <img src={photoUrl(i.thumb || i.photo)} alt={i.color} loading="lazy" className="h-full w-full object-cover" />
            </button>
          ) : (
            <div className="flex h-full items-center justify-center text-sm text-[color:var(--s-mute)]">Photo coming soon</div>
          )}
          <span className="absolute left-3 top-3 flex flex-wrap gap-1.5">
            {i.on_sale && <span data-testid={`public-badge-sale-${i.key}`} className="s-badge s-badge-gold">Sale</span>}
            {i.new_arrival && <span data-testid={`public-badge-new-${i.key}`} className="s-badge">New</span>}
            {i.kind === "Remnant" && <span data-testid={`public-badge-remnant-${i.key}`} className="s-badge">Remnant</span>}
          </span>
        </div>
        <div className="flex min-w-0 flex-col justify-center">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="s-micro !text-[10px] text-[color:var(--s-mute)]">{[i.brand, i.material].filter(Boolean).join(" · ") || i.kind}</div>
              <button type="button" data-testid={`public-name-${i.key}`} onClick={() => onOpen(i)} title={`${i.color} — see details`} className="mt-2 block max-w-full truncate text-left text-2xl font-medium tracking-tight text-[color:var(--s-ink)] transition-colors duration-200 hover:text-[color:var(--s-gold)]">
                {i.color}
              </button>
              <div className="mt-1.5 flex items-center gap-2 text-sm text-[color:var(--s-mute)]">
                <span data-testid={`public-dims-${i.key}`}>{dimsOf(i)}</span>
                <span data-testid={`public-colors-${i.key}`} className="flex shrink-0 items-center gap-1">
                  {[i.primary_color, i.accent_color].filter(Boolean).map((c) => (
                    <span key={c} title={familiesOf(i).join(" / ")} className="h-2.5 w-2.5 rounded-full border border-black/10" style={{ background: c }} />
                  ))}
                </span>
              </div>
              {i.kind === "Remnant" && <p className="mt-1 truncate text-xs text-[color:var(--s-gold)]" data-testid={`remnant-fit-${i.key}`}>{remnantFit(i)}</p>}
            </div>
            <div className="shrink-0 text-right">
              <div className="text-sm text-[color:var(--s-mute)]" data-testid={i.special_order ? `special-order-note-${i.key}` : `public-stock-${i.key}`}>{stockOf(i)}</div>
              <div className="s-num text-xl text-[color:var(--s-ink)]" data-testid={`public-sqft-${i.key}`}>{i.sqft > 0 ? `${i.sqft} ft²` : i.thickness || i.kind}</div>
            </div>
          </div>
          <div className="mt-6 flex flex-wrap items-center gap-6">
            {ask}
            <ArrowLink onClick={() => onOpen(i)} testId={`public-details-btn-${i.key}`}>Details</ArrowLink>
          </div>
        </div>
      </article>
    );
  }

  const photos = i.photos || [];
  return (
    <article data-testid={`public-tile-${i.key}`} className="s-pf group relative overflow-hidden">
      <div className={`s-pf-fig relative ${ratio}`}>
        {i.photo ? (
          <button type="button" data-testid={`public-photo-${i.key}`} onClick={() => onGallery(i)} className="block h-full w-full cursor-zoom-in">
            <img src={photoUrl(i.thumb || i.photo)} alt={i.color} loading="lazy" className="h-full w-full object-cover" />
          </button>
        ) : (
          <div className="flex h-full items-center justify-center text-sm text-white/50">Photo coming soon</div>
        )}

        <button
          type="button"
          data-testid={`public-wish-${i.key}`}
          onClick={() => onWish(i)}
          aria-label={saved ? "Remove from wishlist" : "Save to wishlist"}
          aria-pressed={saved}
          className={`absolute right-3 top-3 z-[3] flex h-10 w-10 items-center justify-center rounded-full bg-[#1a1a18]/40 ring-1 ring-white/20 backdrop-blur-md transition-[opacity,color] duration-300 ${
            saved ? "text-[color:var(--s-gold-bright)]" : "text-white opacity-0 group-hover:opacity-100 focus-visible:opacity-100"
          }`}
        >
          <Heart size={16} weight={saved ? "fill" : "bold"} />
        </button>

        <span className="absolute left-3 top-3 z-[3] flex flex-wrap gap-1.5">
          {i.on_sale && <span data-testid={`public-badge-sale-${i.key}`} className="s-badge s-badge-gold">Sale</span>}
          {i.new_arrival && <span data-testid={`public-badge-new-${i.key}`} className="s-badge s-badge-light">New</span>}
          {i.kind === "Remnant" && <span data-testid={`public-badge-remnant-${i.key}`} className="s-badge s-badge-light">Remnant</span>}
        </span>

        <div className="absolute inset-x-0 bottom-0 z-[2] p-3">
          <div className="bg-[#1a1a18]/85 p-3.5 text-white backdrop-blur-md sm:p-4" data-testid={`public-tile-caption-${i.key}`}>
            <div className="min-w-0">
              <div className="s-micro !text-[10px] text-white/55">{[i.brand, i.material].filter(Boolean).join(" · ") || i.kind}</div>
              <button type="button" data-testid={`public-name-${i.key}`} onClick={() => onOpen(i)} title={`${i.color} — see details`} className="mt-1 block max-w-full truncate text-left text-lg font-medium tracking-tight text-white transition-colors duration-200 hover:text-[color:var(--s-gold-bright)]">
                {i.color}
              </button>
              <div className="mt-1 flex items-center gap-1.5 text-xs text-white/70">
                <span data-testid={`public-sqft-${i.key}`}>{i.sqft > 0 ? `${i.sqft} ft²` : i.thickness || i.kind}</span>
                <span aria-hidden>·</span>
                <span data-testid={`public-dims-${i.key}`} className="truncate">{dimsOf(i)}</span>
                <span data-testid={`public-colors-${i.key}`} className="flex shrink-0 items-center gap-1">
                  {[i.primary_color, i.accent_color].filter(Boolean).map((c) => (
                    <span key={c} title={familiesOf(i).join(" / ")} className="h-2.5 w-2.5 rounded-full ring-1 ring-white/30" style={{ background: c }} />
                  ))}
                </span>
              </div>
              {i.kind === "Remnant" && <p className="mt-1 truncate text-[11px] text-[color:var(--s-gold-bright)]" data-testid={`remnant-fit-${i.key}`}>{remnantFit(i)}</p>}
              <span data-testid={i.special_order ? `special-order-note-${i.key}` : `public-stock-${i.key}`} className="sr-only">{stockOf(i)}</span>
              <button type="button" data-testid={`public-details-btn-${i.key}`} onClick={() => onOpen(i)} className="sr-only">Details</button>
            </div>
          </div>
        </div>
      </div>
      {photos.length > 1 && <span className="sr-only">{photos.length} photos</span>}
    </article>
  );
};

export default function PublicShowroom() {
  const nav = useNavigate();
  const site = useSite();
  const [params] = useSearchParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [q, setQ] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const fromParams = () => ({
    ...EMPTY,
    brand: params.get("brand") ? [params.get("brand")] : [],
    material: params.get("material") ? [params.get("material")] : [],
    type: params.get("type") ? [params.get("type")] : [],
  });
  const [picked, setPicked] = useState(fromParams);
  const [flag, setFlag] = useState(params.get("flag") || "");
  const [panel, setPanel] = useState(false);
  const [view, setView] = useState(() => localStorage.getItem("inventory-view") || "grid");
  const { cols, choose, max: maxCols } = useGridCols("public-showroom-cols", 3);
  const [sort, setSort] = useState("featured");
  const [page, setPage] = useState(1);
  const [shot, setShot] = useState(-1);
  const [hint, setHint] = useState(false);
  const [cursor, setCursor] = useState(-1);
  const [recentKeys, setRecentKeys] = useState(() => getRecent());
  const [dlg, setDlg] = useState(null);
  const gridRef = useRef(null);
  const searchRef = useRef(null);
  const wishlist = useWishlist();
  const saved = useMemo(() => new Set(wishlist.map((w) => w.id)), [wishlist]);

  useEffect(() => {
    if (!panel) return undefined;
    const onKey = (e) => e.key === "Escape" && setPanel(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [panel]);

  useEffect(() => {
    fetch(`${API}/public/showroom`)
      .then((r) => (r.ok ? r.json() : Promise.reject(r)))
      .then(setData)
      .catch(() => setError("This showroom is not available right now."));
  }, []);

  useEffect(() => {
    setPicked(fromParams());
    setFlag(params.get("flag") || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);
  useEffect(() => localStorage.setItem("inventory-view", view), [view]);
  useEffect(() => {
    if (searchOpen) searchRef.current?.focus();
  }, [searchOpen]);

  const all = useMemo(() => data?.items || [], [data]);

  const facets = useMemo(() => {
    const colorCounts = new Map();
    all.forEach((i) => familiesOf(i).forEach((f) => colorCounts.set(f, (colorCounts.get(f) || 0) + 1)));
    return {
      brand: tally(all.map((i) => i.brand)),
      color: FAMILIES.filter(([name]) => colorCounts.get(name)).map(([name]) => [name, colorCounts.get(name)]),
      finish: tally(all.map((i) => i.finish)),
      material: tally(all.map((i) => i.material)),
      type: tally(all.map((i) => i.kind)),
      thickness: tally(all.map((i) => i.thickness)),
    };
  }, [all]);

  const items = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const hit = (list, value) => !list.length || list.includes(value);
    const out = all
      .filter(
        (i) =>
          hit(picked.brand, i.brand) &&
          hit(picked.finish, i.finish) &&
          hit(picked.material, i.material) &&
          hit(picked.type, i.kind) &&
          hit(picked.thickness, i.thickness) &&
          (flag !== "new" || i.new_arrival) &&
          (flag !== "sale" || i.on_sale) &&
          (!picked.color.length || familiesOf(i).some((f) => picked.color.includes(f))),
      )
      .filter((i) => (needle ? `${i.color} ${i.material} ${i.finish} ${i.brand || ""}`.toLowerCase().includes(needle) : true));
    if (sort === "name") out.sort((a, b) => a.color.localeCompare(b.color));
    else if (sort === "sqft") out.sort((a, b) => (b.sqft || 0) - (a.sqft || 0));
    else if (sort === "brand") out.sort((a, b) => (a.brand || "").localeCompare(b.brand || "") || a.color.localeCompare(b.color));
    else if (sort === "new") out.sort((a, b) => Number(!!b.new_arrival) - Number(!!a.new_arrival));
    return out;
  }, [all, q, picked, flag, sort]);

  useEffect(() => setPage(1), [q, picked, flag, sort]);
  const pages = Math.max(1, Math.ceil(items.length / PAGE));
  const visible = useMemo(() => items.slice((page - 1) * PAGE, page * PAGE), [items, page]);
  const goPage = (n) => {
    setPage(Math.min(pages, Math.max(1, n)));
    scrollToTopOf(gridRef.current);
  };

  const [galleryKey, setGalleryKey] = useState("");
  const gallery = useMemo(() => {
    const i = all.find((x) => x.key === galleryKey);
    if (!i) return [];
    return (i.photos || []).map((p, n) => ({
      path: p.path,
      thumb: p.thumb,
      key: i.key,
      title: i.color,
      sub: `${i.kind}${i.sqft > 0 ? ` · ${i.size} · ${i.sqft} ft²` : ""}${(i.photos || []).length > 1 ? ` · photo ${n + 1}` : ""}`,
    }));
  }, [all, galleryKey]);
  const openGallery = (item) => {
    if (!(item.photos || []).length) return;
    setGalleryKey(item.key);
    setShot(0);
  };

  const hints = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (needle.length < 2) return [];
    const seen = new Set();
    const out = [];
    for (const i of all) {
      const name = (i.color || "").toLowerCase();
      const match = `${i.color} ${i.material} ${i.finish} ${i.brand || ""}`.toLowerCase().includes(needle);
      if (!match || seen.has(name)) continue;
      seen.add(name);
      out.push({ ...i, pieces: all.filter((x) => (x.color || "").toLowerCase() === name).length });
      if (out.length === 6) break;
    }
    return out;
  }, [all, q]);

  const recent = useMemo(() => recentKeys.map((k) => all.find((i) => i.key === k)).filter(Boolean), [all, recentKeys]);

  const goTo = (item) => {
    setHint(false);
    nav(`/inventory/${item.key}`);
  };
  const onSearchKeys = (e) => {
    if (!hints.length) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHint(true);
      setCursor((n) => (n + 1) % hints.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setCursor((n) => (n <= 0 ? hints.length - 1 : n - 1));
    } else if (e.key === "Enter" && cursor >= 0) {
      e.preventDefault();
      goTo(hints[cursor]);
    } else if (e.key === "Escape") {
      setHint(false);
    }
  };

  const toggle = (group, value) =>
    setPicked((p) => ({ ...p, [group]: p[group].includes(value) ? p[group].filter((x) => x !== value) : [...p[group], value] }));
  const copy = pageCopy({
    material: picked.material.length === 1 ? picked.material[0] : "",
    type: picked.type.length === 1 ? picked.type[0] : "",
    flag,
  });
  const activeCount = Object.values(picked).reduce((n, v) => n + v.length, 0) + (flag ? 1 : 0);

  usePageHead({ title: copy.seo?.title, description: copy.seo?.description, suffix: site?.shop?.name || "Brioni Granite" });
  const clearAll = () => {
    setPicked({ ...EMPTY });
    setFlag("");
    nav("/inventory", { replace: true });
  };
  const wish = (i) => {
    const on = toggleWishlist({ id: i.key, key: i.key, color: i.color, size: i.size, thumb: i.thumb || i.photo });
    toast.success(on ? "Saved to your wishlist" : "Removed from your wishlist");
  };
  const holdMessage = (i) => {
    const d = dimsOf(i).replace("Size on request", "").replace(/^ · /, "");
    return `Please put a hold on ${i.color}${d ? ` (${d})` : ""} for me.`;
  };

  if (error)
    return (
      <div className="site flex min-h-screen items-center justify-center p-6 text-center">
        <p className="text-sm text-[color:var(--s-mute)]" data-testid="showroom-unavailable">{error}</p>
      </div>
    );

  const tileProps = { onGallery: openGallery, onOpen: (i) => nav(`/inventory/${i.key}`), onAsk: (item, hold) => setDlg({ item, hold }), onWish: wish };

  const chips = [["All projects", ""], ...facets.material.map(([m, c]) => [m, m, c])];

  return (
    <SiteShell site={site} testId="public-showroom">
      <main className="s-dark-page s-dark-tex bg-[color:var(--s-bg)] px-6 pb-24 pt-8 sm:pt-12 lg:px-12">
        <div className="w-full">
          <nav className="s-micro flex items-center gap-2 !text-[10px] text-[color:var(--s-mute)]" aria-label="Breadcrumb" data-testid="showroom-crumbs">
            <Link to="/" className="hover:text-[color:var(--s-ink)]">Home</Link>
            <span aria-hidden="true">/</span>
            <span className="text-[color:var(--s-ink)]">Inventory</span>
            {picked.material.length === 1 && (
              <>
                <span aria-hidden="true">/</span>
                <span className="text-[color:var(--s-ink)]">{picked.material[0]}</span>
              </>
            )}
          </nav>

          {/* Nordic portfolio header */}
          <div className="mt-8 flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between" data-testid="public-showroom-hero">
            <div>
              <span className="s-eyebrow">Inventory</span>
              <h1 className="s-hx s-spaced mt-5 break-words text-5xl text-[color:var(--s-ink)] sm:text-7xl lg:text-8xl" data-testid="showroom-headline">
                <SplitText text={copy.headline} />
              </h1>
              <p className="mt-6 max-w-2xl text-base leading-relaxed text-[color:var(--s-mute)]" data-testid="showroom-blurb">{copy.blurb}</p>
            </div>
            <p className="inline-flex items-center gap-3 whitespace-nowrap text-sm text-[color:var(--s-ink)]" data-testid="public-showroom-live">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[color:var(--s-gold-bright)] opacity-70" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-[color:var(--s-gold-bright)]" />
              </span>
              <span className="s-num text-2xl">{all.length}</span>
              <span className="text-[color:var(--s-mute)]">{all.length === 1 ? "piece" : "pieces"} on the racks today</span>
            </p>
          </div>

          {/* top row: category chips + search button */}
          <div className="mt-10 flex flex-col gap-4 border-y border-[color:var(--s-line)] py-4 lg:flex-row lg:items-center lg:justify-between" data-testid="showroom-categories">
            <div className="no-bar -mx-1 flex gap-x-6 gap-y-2 overflow-x-auto px-1">
              {chips.map(([label, val, count]) => {
                const on = val ? picked.material.length === 1 && picked.material[0] === val : picked.material.length === 0;
                const cnt = val ? count : all.length;
                return (
                  <button
                    key={val || "__all__"}
                    type="button"
                    aria-pressed={on}
                    data-testid={`showroom-cat-${val ? val.toLowerCase().replace(/[^a-z0-9]+/g, "-") : "all"}`}
                    onClick={() => setPicked((p) => ({ ...p, material: val ? [val] : [], type: [] }))}
                    className={`s-micro flex shrink-0 items-center gap-2 whitespace-nowrap py-2 !text-[12px] tracking-[0.1em] transition-colors duration-300 ${on ? "text-[color:var(--s-ink)]" : "text-[color:var(--s-mute)] hover:text-[color:var(--s-ink)]"}`}
                  >
                    <span className={`h-1.5 w-1.5 rounded-full transition-colors duration-300 ${on ? "bg-[color:var(--s-gold-bright)]" : "bg-transparent"}`} />
                    {label}
                    <span className="text-[color:var(--s-mute)]">({cnt})</span>
                  </button>
                );
              })}
            </div>
            <button
              type="button"
              data-testid="showroom-search-btn"
              onClick={() => setSearchOpen((v) => !v)}
              aria-expanded={searchOpen}
              className="s-micro inline-flex shrink-0 items-center gap-2 self-start rounded-full border border-[color:var(--s-line)] px-5 py-2.5 !text-[11px] text-[color:var(--s-ink)] transition-colors duration-300 hover:border-[color:var(--s-gold-bright)] hover:bg-[color:var(--s-gold-bright)] hover:text-[#1a1a18] lg:self-auto"
            >
              {searchOpen ? <X size={14} weight="bold" /> : <MagnifyingGlass size={14} weight="bold" />}
              {searchOpen ? "Close search" : "Search"}
            </button>
          </div>

          {/* expanding search field */}
          <div className={`grid transition-all duration-500 ${searchOpen ? "mt-6 grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"}`}>
            <div className="overflow-visible">
              <div className="relative">
                <span className="pointer-events-none absolute left-4 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center text-[color:var(--s-mute)]">
                  <MagnifyingGlass size={18} weight="bold" />
                </span>
                <input
                  ref={searchRef}
                  data-testid="public-showroom-search"
                  value={q}
                  onChange={(e) => {
                    setQ(e.target.value);
                    setHint(true);
                    setCursor(-1);
                  }}
                  onFocus={() => setHint(true)}
                  onBlur={() => setTimeout(() => setHint(false), 140)}
                  onKeyDown={onSearchKeys}
                  autoComplete="off"
                  placeholder="Search a color, material or brand"
                  className="h-14 w-full border border-[color:var(--s-line)] bg-[color:var(--s-surface)] pl-12 pr-12 text-base outline-none transition-colors duration-200 focus:border-[color:var(--s-ink)] placeholder:text-[color:var(--s-mute)]"
                />
                {q && (
                  <button type="button" data-testid="search-clear" onClick={() => setQ("")} aria-label="Clear search" className="absolute right-3 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center text-[color:var(--s-mute)] transition-colors duration-200 hover:text-[color:var(--s-ink)]">
                    <X size={16} weight="bold" />
                  </button>
                )}
                {hint && hints.length > 0 && (
                  <div data-testid="search-suggestions" className="absolute left-0 right-0 top-full z-40 mt-2 border border-[color:var(--s-line)] bg-[color:var(--s-surface)] p-2 shadow-[0_30px_60px_-30px_rgba(35,30,20,0.5)]">
                    {hints.map((h, n) => (
                      <button key={h.key} type="button" data-testid={`search-suggestion-${h.key}`} onMouseEnter={() => setCursor(n)} onClick={() => goTo(h)} className={`flex w-full items-center gap-4 px-3 py-2.5 text-left transition-colors duration-150 ${cursor === n ? "bg-[color:var(--s-cream)]" : ""}`}>
                        <span className="h-12 w-12 shrink-0 overflow-hidden bg-[color:var(--s-cream)]">
                          {h.photo && <img src={photoUrl(h.thumb || h.photo, 700)} alt={h.color} className="h-full w-full object-cover" />}
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-sm font-semibold text-[color:var(--s-ink)]">{h.color}</span>
                          <span className="block truncate text-xs text-[color:var(--s-mute)]">{[h.material, h.finish, h.kind].filter(Boolean).join(" · ")}</span>
                        </span>
                        <span className="shrink-0 bg-[color:var(--s-cream)] px-2.5 py-1 text-xs font-medium text-[color:var(--s-mute)]">{h.pieces > 1 ? `${h.pieces} pieces` : "View"}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* left filter + masonry */}
          <div className="mt-8 lg:grid lg:grid-cols-[280px_1fr] lg:gap-10 xl:gap-14" ref={gridRef}>
            <aside className="hidden lg:block" data-testid="showroom-sidebar-persistent">
              <div className="sticky top-28">
                <InventoryFilters facets={facets} picked={picked} toggle={toggle} clear={clearAll} activeCount={activeCount} />
              </div>
            </aside>
            <div className="min-w-0">
              <ShopToolbar
                prefix="showroom"
                start={items.length ? (page - 1) * PAGE + 1 : 0}
                end={Math.min(page * PAGE, items.length)}
                total={items.length}
                extra={
                  activeCount > 0 && (
                    <button type="button" data-testid="showroom-clear-all" onClick={clearAll} className="s-micro inline-flex items-center gap-2 !text-[10px] text-[color:var(--s-mute)] transition-colors duration-200 hover:text-[color:var(--s-ink)]">
                      Clear {activeCount} filter{activeCount === 1 ? "" : "s"} <X size={12} weight="bold" />
                    </button>
                  )
                }
                sort={sort}
                onSort={setSort}
                sortOptions={SORTS}
                view={view}
                onView={setView}
                cols={cols}
                onCols={choose}
                maxCols={maxCols}
                panel={panel}
                onPanel={() => setPanel((v) => !v)}
                activeCount={activeCount}
                filterAlways={false}
              />

              {items.length > 0 && (
                view === "list" ? (
                  <div className="mt-10 flex flex-col gap-8" data-testid="public-showroom-grid" data-view="list">
                    {visible.map((i) => (
                      <Tile key={i.key} i={i} list {...tileProps} saved={saved.has(i.key)} />
                    ))}
                  </div>
                ) : (
                  <div className="mt-10 grid gap-6" style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }} data-testid="public-showroom-grid" data-view="grid" data-cols={cols}>
                    {visible.map((i, n) => (
                      <Tile key={i.key} i={i} list={false} ratio="aspect-[4/5]" {...tileProps} saved={saved.has(i.key)} />
                    ))}
                  </div>
                )
              )}

              <Pager page={page} pages={pages} onPage={goPage} prefix="showroom" />

              {items.length === 0 && (
                <div className="mt-10 flex flex-col items-center border border-dashed border-[color:var(--s-line)] px-6 py-16 text-center sm:px-8 sm:py-24" data-testid="public-showroom-empty">
                  <h3 className="s-hx text-4xl sm:text-5xl">{activeCount || q ? "No match on the racks." : "Restocking."}</h3>
                  <p className="mt-5 max-w-md text-base leading-relaxed text-[color:var(--s-mute)]">
                    {activeCount || q
                      ? "Nothing in stock matches that just now — new containers land often, and we can source a color for you."
                      : "The racks are being restocked. Call the shop and we will walk you through what is landing."}
                  </p>
                  <div className="mt-10 flex flex-wrap justify-center gap-6">
                    {activeCount > 0 && <button type="button" data-testid="empty-clear-filters" onClick={clearAll} className="s-ghost">Clear filters</button>}
                    <Link to="/contact" className="s-btn">Ask us to source it</Link>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      {/* mobile filter drawer */}
      <div className={`fixed inset-0 z-[80] overflow-hidden ${panel ? "" : "pointer-events-none"}`} aria-hidden={!panel} inert={panel ? undefined : true} data-testid="showroom-filter-root">
        <button type="button" aria-label="Close filters" onClick={() => setPanel(false)} data-testid="showroom-filter-backdrop" className={`absolute inset-0 bg-[#1a1a18]/50 transition-opacity duration-500 ${panel ? "opacity-100" : "opacity-0"}`} />
        <aside data-testid="showroom-sidebar" className={`site s-dark-page no-bar absolute inset-y-0 right-0 flex w-full max-w-[440px] flex-col overflow-y-auto bg-[color:var(--s-bg)] px-8 pb-12 pt-7 text-[color:var(--s-ink)] shadow-[-40px_0_80px_-40px_rgba(0,0,0,0.5)] transition-transform duration-600 ease-[cubic-bezier(.2,.7,.2,1)] sm:px-10 ${panel ? "translate-x-0" : "translate-x-full"}`}>
          <div className="flex items-center justify-between">
            <span className="s-micro text-[color:var(--s-mute)]">{items.length} {items.length === 1 ? "result" : "results"}</span>
            <button type="button" onClick={() => setPanel(false)} data-testid="showroom-filter-close" className="s-micro flex items-center gap-2 transition-colors duration-300 hover:text-[color:var(--s-gold)]">Close <X size={16} weight="bold" /></button>
          </div>
          <h2 className="s-hx mt-10 text-4xl" data-testid="showroom-filter-title">Filter <strong>stone.</strong></h2>
          <InventoryFilters facets={facets} picked={picked} toggle={toggle} clear={clearAll} activeCount={activeCount} />
          <div className="mt-10 grid gap-4">
            <button type="button" data-testid="showroom-filter-apply" onClick={() => setPanel(false)} className="s-ghost w-full">Show {items.length} {items.length === 1 ? "slab" : "slabs"}</button>
            {activeCount > 0 && <button type="button" data-testid="showroom-filter-reset" onClick={clearAll} className="s-micro text-center !text-[11px] text-[color:var(--s-mute)] transition-colors duration-300 hover:text-[color:var(--s-ink)]">Clear all filters</button>}
          </div>
        </aside>
      </div>

      {recent.length > 1 && (
        <section className="s-dark-page s-dark-tex border-t border-white/10 px-6 py-16 lg:px-12" data-testid="recently-viewed">
          <div className="w-full">
            <div className="flex items-baseline justify-between">
              <h2 className="s-hx text-3xl sm:text-4xl">Recently viewed.</h2>
              <button type="button" data-testid="recent-clear" onClick={() => { clearRecent(); setRecentKeys([]); }} className="s-micro text-[color:var(--s-mute)] transition-colors duration-200 hover:text-[color:var(--s-ink)]">Clear</button>
            </div>
            <div className="no-bar mt-7 flex gap-5 overflow-x-auto pb-2">
              {recent.map((i) => (
                <Link key={i.key} to={`/inventory/${i.key}`} data-testid={`recent-${i.key}`} className="group w-40 shrink-0">
                  <span className="s-fig block aspect-[399/449] bg-[color:var(--s-line)]">
                    {i.photo && <img src={photoUrl(i.thumb || i.photo, 700)} alt={i.color} className="h-full w-full object-cover" />}
                  </span>
                  <span className="mt-3 block truncate text-sm text-[color:var(--s-ink)]">{i.color}</span>
                  <span className="block truncate text-xs text-[color:var(--s-mute)]">{[i.material, i.kind === "Remnant" ? "Remnant" : ""].filter(Boolean).join(" · ")}</span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <QuoteDialog
        open={!!dlg}
        onClose={() => setDlg(null)}
        color={dlg?.item.color || ""}
        message={dlg ? (dlg.hold ? holdMessage(dlg.item) : `I'd like a quote for ${dlg.item.color}.`) : ""}
        source={dlg?.hold ? "Slab hold request" : ""}
        title={dlg ? `${dlg.hold ? "Hold" : "Quote"} ${dlg.item.color}` : ""}
        testId="showroom-hold-dialog"
      />

      {shot >= 0 && gallery.length > 0 && (
        <PortalLightbox prefix="public-lightbox" shots={gallery} index={shot} srcOf={(path) => `${API}/public/showroom/photo/${path}`} onClose={() => setShot(-1)} />
      )}
    </SiteShell>
  );
}
